/**
 * 播放策略接口（基类）
 * 所有播放策略都必须实现此接口
 */
export class PlayStrategy {
  /**
   * 加载歌曲
   * @param {Object} song - 歌曲对象
   * @returns {Promise<void>}
   */
  async load(song) {
    throw new Error('load() 方法必须由子类实现');
  }
  
  /**
   * 播放
   * @returns {Promise<void>}
   */
  async play() {
    throw new Error('play() 方法必须由子类实现');
  }
  
  /**
   * 暂停
   */
  pause() {
    throw new Error('pause() 方法必须由子类实现');
  }
  
  /**
   * 停止
   */
  stop() {
    throw new Error('stop() 方法必须由子类实现');
  }
  
  /**
   * 设置播放时间
   * @param {number} time - 播放时间（秒）
   */
  setCurrentTime(time) {
    throw new Error('setCurrentTime() 方法必须由子类实现');
  }
  
  /**
   * 获取当前播放时间
   * @returns {number}
   */
  getCurrentTime() {
    throw new Error('getCurrentTime() 方法必须由子类实现');
  }
  
  /**
   * 获取总时长
   * @returns {number}
   */
  getDuration() {
    throw new Error('getDuration() 方法必须由子类实现');
  }
  
  /**
   * 是否正在播放
   * @returns {boolean}
   */
  isPlaying() {
    throw new Error('isPlaying() 方法必须由子类实现');
  }
  
  /**
   * 监听事件
   * @param {string} event - 事件名称
   * @param {Function} callback - 回调函数
   */
  on(event, callback) {
    if (!this.eventCallbacks) {
      this.eventCallbacks = {};
    }
    if (!this.eventCallbacks[event]) {
      this.eventCallbacks[event] = [];
    }
    this.eventCallbacks[event].push(callback);
  }
  
  /**
   * 移除事件监听
   * @param {string} event - 事件名称
   * @param {Function} callback - 回调函数
   */
  off(event, callback) {
    if (!this.eventCallbacks || !this.eventCallbacks[event]) {
      return;
    }
    const index = this.eventCallbacks[event].indexOf(callback);
    if (index > -1) {
      this.eventCallbacks[event].splice(index, 1);
    }
  }
  
  /**
   * 触发事件
   * @param {string} event - 事件名称
   * @param {*} data - 事件数据
   */
  emit(event, data) {
    if (!this.eventCallbacks || !this.eventCallbacks[event]) {
      if (event === 'albumMediaChanged') {
        console.warn('⚠️ [PlayStrategy] albumMediaChanged 事件没有监听器！');
      }
      return;
    }
    const listenerCount = this.eventCallbacks[event].length;
    if (event === 'albumMediaChanged') {
      console.log(`🖼️ [PlayStrategy] 触发 albumMediaChanged 事件，监听器数量: ${listenerCount}`);
    }
    this.eventCallbacks[event].forEach((callback, index) => {
      try {
        if (event === 'albumMediaChanged') {
          console.log(`🖼️ [PlayStrategy] 调用监听器 ${index + 1}/${listenerCount}`);
        }
        callback(data);
      } catch (error) {
        console.error(`🎵 [PlayStrategy] 事件回调错误 [${event}] (监听器 ${index + 1}/${listenerCount}):`, error);
      }
    });
  }
  
  /**
   * 清理资源
   */
  cleanup() {
    // 清空所有事件监听器
    if (this.eventCallbacks) {
      Object.keys(this.eventCallbacks).forEach(event => {
        this.eventCallbacks[event] = [];
      });
      this.eventCallbacks = {};
    }
  }
  
  /**
   * 🔥 新增：全屏相关方法
   * 子类可以重写这些方法来实现自己的全屏逻辑
   */
  
  /**
   * 检查是否处于全屏状态
   * @returns {boolean}
   */
  isInFullscreen() {
    return false; // 默认未实现
  }
  
  /**
   * 进入全屏（如果需要的话）
   * 当切换歌曲时，如果之前处于全屏状态，会调用此方法
   * @returns {Promise<void>}
   */
  async enterFullscreenIfNeeded() {
    // 默认不实现，子类可以重写
  }
  
  /**
   * 退出全屏
   * @returns {Promise<void>}
   */
  async exitFullscreen() {
    // 默认不实现，子类可以重写
  }
  
  /**
   * 设置全屏控制接口（由组件传入）
   * 策略可以通过这个接口控制组件的全屏状态
   * @param {Object} fullscreenControls - 全屏控制接口对象
   * @param {Function} fullscreenControls.enterFullscreen - 进入全屏的方法
   * @param {Function} fullscreenControls.exitFullscreen - 退出全屏的方法
   * @param {Function} fullscreenControls.isInFullscreen - 检查是否处于全屏的方法
   */
  setFullscreenControls(fullscreenControls) {
    this.fullscreenControls = fullscreenControls;
  }
}

